import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import ReactDOM from "react-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { PostAsync } from "../../../common/httpRequests";
import { BACKEND } from "../../../constants/settings";
import { uploadState } from "../../../features/pageSlice";
import useAuth from "hooks/useAuth";
import CodeInput from "./CodeInput";

const schema = yup
  .object({
    code1: yup.number().integer().max(9).min(0).required(),
    code2: yup.number().integer().max(9).min(0).required(),
    code3: yup.number().integer().max(9).min(0).required(),
    code4: yup.number().integer().max(9).min(0).required(),
    code5: yup.number().integer().max(9).min(0).required(),
    code6: yup.number().integer().max(9).min(0).required(),
  })
  .required();
type FormData = yup.InferType<typeof schema>;

export default function CodeForm(props: any) {
  const { email, handleSendEmail } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [deletedField, setDeletedField] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const { fetchSupplierData } = useAuth();

  const handleVerifyCode = async (data) => {
    setIsLoading(true);
    const code =
      data.code1.toString() +
      data.code2.toString() +
      data.code3.toString() +
      data.code4.toString() +
      data.code5.toString() +
      data.code6.toString();
    const currentSupplierId = window.location.pathname.split("/")[1];
    const request = {
      id: currentSupplierId,
      code: code,
    };
    const res = await PostAsync(
      `${BACKEND}/api/vendorservice/suppliers/login_v2`,
      request,
    );
    if (res.status === 200 && res.data) {
      localStorage.setItem("token", res.data?.token);
      localStorage.setItem("code", code);
      localStorage.setItem("supplierId", currentSupplierId);
      localStorage.setItem(
        "date",
        (Number(new Date().getTime()) / 3.6e6).toString(),
      );
      if (!localStorage.getItem("version") && res.data?.version) {
        localStorage.setItem("version", res.data?.version);
      }
      const freshSupplier = await fetchSupplierData();
      setIsLoading(false);
      dispatch(uploadState());
      if (freshSupplier?.isOnboardingDone) {
        return navigate(`/${currentSupplierId}/documents`);
      } else {
        navigate(`/${currentSupplierId}/onboarding`);
      }
    } else {
      setIsLoading(false);
      return enqueueSnackbar(t("email.wrongCode"), {
        variant: "error",
      });
    }
  };

  const handleResend = (e: React.MouseEvent<HTMLButtonElement>) => {
    reset();
    handleSendEmail(e);
  };

  const handleDelete = (event) => {
    if (event.code === "Backspace") {
      let step = Number(event.target.id.slice(-1));
      if (step !== 1 && deletedField === step) {
        (
          ReactDOM.findDOMNode(
            document.querySelector(`#code${step - 1}`),
          ) as HTMLElement
        )?.focus();
      } else if (step !== 1) {
        setDeletedField(step);
      }
    }
  };

  const handleFill = (value) => {
    let step = Number(value.target.id.slice(-1));
    let input = value.target.value;

    const digit =
      input.length >= 2 ? Number(input[input.length - 1]) : Number(input);

    setValue(`code${step}` as keyof FormData, digit);
    value.target.value = digit;
    if (value.target.value && step !== 6) {
      (
        ReactDOM.findDOMNode(
          document.querySelector(`#code${step + 1}`),
        ) as HTMLElement
      )?.focus();
    } else if (value.target.value) {
      setTimeout(() => {
        document.querySelector("form")?.requestSubmit();
      }, 0);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    let paste = event.clipboardData.getData("text");
    const separatedNumbers = paste.toString().split("");
    if (separatedNumbers.length >= 6) {
      [...Array(6)].forEach((_, i) => {
        const currentCodeInput = document.querySelector(`#code${i + 1}`);
        if (currentCodeInput) {
          (currentCodeInput as HTMLInputElement).value = separatedNumbers[i];
          setValue(
            `code${i + 1}` as keyof FormData,
            Number(separatedNumbers[i]),
          );
          (ReactDOM.findDOMNode(currentCodeInput) as HTMLElement)?.focus();
        }
      });
    }
    setTimeout(() => {
      document.querySelector("form")?.requestSubmit();
    }, 0);
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap="24px"
      sx={{ width: "100%" }}
    >
      <form
        action="/"
        method="POST"
        onSubmit={handleSubmit((d) => handleVerifyCode(d))}
      >
        <Box display={"flex"} flexDirection={"column"} gap="24px">
          <Box display={"flex"} flexDirection={"column"} gap="12px">
            <Typography variant="h4" fontWeight={700}>
              {t("email.checkEmail")}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              color={theme.palette.text.label}
            >
              {t("email.checkEmailCtx1")}{" "}
              <Box
                component="span"
                sx={{ color: theme.palette.secondary.dark }}
              >
                {email}
              </Box>
            </Typography>
          </Box>
          <Box>
            <Typography color={"#7c8a96"}>
              {errors.code1?.message}
              {errors.code2?.message}
              {errors.code3?.message}
              {errors.code4?.message}
              {errors.code5?.message}
              {errors.code6?.message}
            </Typography>
            <Box display="flex" justifyContent={"space-between"} gap="8px">
              <CodeInput
                register={register}
                position="code1"
                handleFill={handleFill}
                handlePaste={handlePaste}
                disabled={isLoading}
              />
              <CodeInput
                register={register}
                position="code2"
                handleFill={handleFill}
                handleDelete={handleDelete}
                disabled={isLoading}
              />
              <CodeInput
                register={register}
                position="code3"
                handleFill={handleFill}
                handleDelete={handleDelete}
                disabled={isLoading}
              />
              <CodeInput
                register={register}
                position="code4"
                handleFill={handleFill}
                handleDelete={handleDelete}
                disabled={isLoading}
              />
              <CodeInput
                register={register}
                position="code5"
                handleFill={handleFill}
                handleDelete={handleDelete}
                disabled={isLoading}
              />
              <CodeInput
                register={register}
                position="code6"
                handleFill={handleFill}
                handleDelete={handleDelete}
                disabled={isLoading}
              />
            </Box>
          </Box>
        </Box>
      </form>
      <Typography
        sx={{
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          color: theme.palette.text.label,
        }}
      >
        {t("email.resend")}{" "}
        <Button
          sx={{
            padding: 0,
            paddingLeft: 0.5,
            minWidth: 0,
            fontSize: "14px",
            textTransform: "none",
            color: theme.palette.secondary.dark,
          }}
          onClick={handleResend}
        >
          {t("email.resendLink")}
        </Button>
      </Typography>
      <LoadingButton
        fullWidth
        variant="contained"
        loading={isLoading}
        onClick={() => document.querySelector("form")?.requestSubmit()}
        sx={{ height: 48, borderRadius: "8px" }}
      >
        {t("email.verify")}
      </LoadingButton>
    </Box>
  );
}
